import { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Stack,
  Typography,
  Tooltip,
  TextField,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';

import { AddCircle, Edit, SearchNormal1, Bag, DocumentCopy } from 'iconsax-react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import useAuth from 'hooks/useAuth';
import Snackbar from 'utils/Snackbar';

import MainCard from 'components/MainCard';
import BackToggle from 'components/third-party/dropzone/BackToggle';
import CustomTable from 'components/@extended/CustomTable';
import SkeletonTable from 'components/SkeletonTable';
import IconButton from 'components/@extended/IconButton';

import { getAllPerformanceCriteria, deletePerformanceCriterion } from '../../services/performanceCriteria.service';

const PerformanceCriteriaList = () => {
  const navigate = useNavigate();
  const { organization } = useAuth();

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [pageIndex, setPageIndex] = useState(1);
  const [size, setSize] = useState(10);
  const [sortArray, setSortArray] = useState<any[]>([]);
  const [data, setData] = useState<any[]>([]);
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedPC, setSelectedPC] = useState<any>(null);

  const fetchPerformanceCriteria = async () => {
    if (!organization?._id) return;

    try {
      setIsLoading(true);

      const reqParams = {
        deleted: false,
        skip: pageIndex,
        limit: size,
        search
      };

      const res = await getAllPerformanceCriteria(reqParams);

      if (res?.status === 200) {
        setData(res?.data?.data?.performanceCriteria || res?.data?.data || []);
        setCount(res?.data?.count || res?.data?.data?.length || 0);
      }
    } catch (err) {
      console.error('Failed to load Performance Criteria:', err);
      Snackbar('Failed to load Performance Criteria', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (organization?._id) {
      fetchPerformanceCriteria();
    }
  }, [organization?._id, pageIndex, size, search]);

  const handleSortChange = (column: any) => {
    const id = column.name || column.id || column.accessor;
    const index = sortArray.findIndex((item) => item.id === id);
    const next = [...sortArray];

    if (index === -1) {
      next.push({ id, sort: true, desc: false });
    } else if (!next[index].desc) {
      next[index].desc = true;
    } else {
      next.splice(index, 1);
    }

    setSortArray(next);
  };

  const handleConfirmDelete = async () => {
    if (!selectedPC?._id) return;

    try {
      await deletePerformanceCriterion(selectedPC._id);
      Snackbar('Performance Criterion deleted successfully', 'success');
      setOpenDeleteDialog(false);
      setSelectedPC(null);
      fetchPerformanceCriteria();
    } catch (err: any) {
      Snackbar(err?.response?.data?.message || 'Failed to delete', 'error');
    }
  };

  const handleCancelDelete = () => {
    setOpenDeleteDialog(false);
    setSelectedPC(null);
  };

  const columns = useMemo(
    () => [
      {
        Header: 'PC Name',
        accessor: 'pcName'
      },
      {
        Header: 'PC Code',
        accessor: 'pcCode'
      },
      {
        Header: 'Version',
        accessor: (row: any) => row?.version ?? '1'
      },
      {
        Header: 'Total Marks',
        accessor: 'totalMarks'
      },
      {
        Header: 'Min Marks',
        accessor: (row: any) => (row?.isCritical ? row?.minMarks ?? '—' : 0)
      },
      {
        Header: 'Type',
        accessor: 'type',
        Cell: ({ value }: any) => {
          if (value === null || value === undefined || value === '') return '—';

          // New format: array of { title, marks }
          if (Array.isArray(value)) {
            const parts = value
              .map((item) => {
                if (!item) return null;
                const rawTitle = item.title ?? item.type ?? '';
                if (!rawTitle) return null;
                const title = rawTitle.charAt(0).toUpperCase() + rawTitle.slice(1);
                return `${title}`;
              })
              .filter(Boolean);

            return parts.length ? parts.join(', ') : '—';
          }

          // Legacy format: simple string like "practical" / "theory"
          if (typeof value === 'string') {
            return value ? value.charAt(0).toUpperCase() + value.slice(1) : '—';
          }

          // Fallback for any unexpected format
          const str = String(value);
          return str ? str.charAt(0).toUpperCase() + str.slice(1) : '—';
        }
      },
      {
        Header: 'Created By',
        accessor: (row: any) => {
          const createdBy = row?.createdBy;
          if (!createdBy) return '—';

          const fullName = `${createdBy?.firstName ?? ''} ${createdBy?.lastName ?? ''}`.trim();
          const title = createdBy?.email ?? fullName ?? '—';

          return (
            <Tooltip title={title}>
              <span>{fullName || createdBy?.email || '—'}</span>
            </Tooltip>
          );
        }
      },
      {
        Header: 'Critical',
        accessor: 'isCritical',
        Cell: ({ value }: any) => (
          <Typography
            variant="caption"
            sx={{
              px: 1.2,
              py: 0.5,
              borderRadius: 1,
              fontWeight: 600,
              color: value ? 'success.main' : 'error.main',
              backgroundColor: value ? 'success.lighter' : 'error.lighter',
              display: 'inline-block'
            }}
          >
            {value ? 'Yes' : 'No'}
          </Typography>
        )
      },
      {
        Header: 'Actions',
        id: 'actions',
        disableSortBy: true,
        Cell: ({ row }: any) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Create new version">
              <IconButton
                color="secondary"
                onClick={() =>
                  navigate('/performanceCriteria/add', {
                    state: { createNewVersionFrom: row.original }
                  })
                }
              >
                <DocumentCopy />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton
                color="primary"
                onClick={() =>
                  navigate('/performanceCriteria/edit', {
                    state: { isEdit: true, data: row.original }
                  })
                }
              >
                <Edit />
              </IconButton>
            </Tooltip>

            <Tooltip title="Delete">
              <IconButton
                color="error"
                onClick={() => {
                  setSelectedPC(row.original);
                  setOpenDeleteDialog(true);
                }}
              >
                <Bag />
              </IconButton>
            </Tooltip>
          </Stack>
        )
      }
    ],
    [navigate]
  );

  return (
    <>
      <Box display="flex" flexDirection="column" gap={1.5}>
        <MainCard
          title={
            <Stack direction="row" alignItems="center" spacing={2}>
              <BackToggle show />
              <Box>
                <Typography variant="h5">Performance Criteria List</Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage Performance Criteria for NOS
                </Typography>
              </Box>
            </Stack>
          }
          content={false}
          secondary={
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search PCs..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setPageIndex(1);
                    setSearch(searchInput);
                  }
                }}
              />
              <IconButton
                variant="outlined"
                onClick={() => {
                  setPageIndex(1);
                  setSearch(searchInput);
                }}
                disabled={!searchInput}
              >
                <SearchNormal1 />
              </IconButton>
              <IconButton variant="dashed" onClick={() => navigate('/performanceCriteria/add')}>
                <AddCircle />
              </IconButton>
            </Stack>
          }
        />

        <MainCard content={false}>
          {isLoading ? (
            <SkeletonTable columns={columns} />
          ) : (
            <CustomTable
              columns={columns}
              data={data}
              sortArray={sortArray}
              count={count}
              size={size}
              pageIndex={pageIndex}
              sort={sortArray}
              setSortingData={handleSortChange}
              handlePageChange={(event: any, page: number) => setPageIndex(page)}
              handleSizeChange={(e: SelectChangeEvent<number>) => {
                setSize(Number(e.target.value));
                setPageIndex(1);
              }}
            />
          )}
        </MainCard>
      </Box>

      <Dialog open={openDeleteDialog} onClose={handleCancelDelete}>
        <DialogTitle>Delete Performance Criterion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{' '}
            <strong>{selectedPC?.pcStatement?.substring(0, 60) || selectedPC?.pcCode || 'this criterion'}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleConfirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PerformanceCriteriaList;
